import {DateTime} from "luxon";
import {first, isEqual} from "lodash";
import {inject, injectable} from "inversify";
import {action, computed, makeAutoObservable, observable, reaction} from "mobx";
import {ViewController} from "data/types/structure";
import {Bindings} from "data/constants/bindings";
import {type IChecksums, type IChecksumStore} from "data/stores/checksum/checksum.store";
import {type IRoundsStore} from "data/stores/rounds/rounds.store";
import {RoundStatus} from "data/enums";

/**
 * Constant for determine update frequency.
 */
const LIVE_SCORING_FETCH_TIMING = 1000 * 60;
const MAX_TIMER_DELAY = 2147483647;

type IChecksumAction = Record<keyof IChecksums, () => Promise<void>>;

export interface ILiveScoreController extends ViewController {
	subscribeLiveScoring: (location?: string) => void;
	unsubscribeLiveScoring: () => void;
}

@injectable()
export class LiveScoreController implements ILiveScoreController {
	protected readonly _actions: IChecksumAction;
	private _timerId?: number;
	@observable protected _interval?: ReturnType<typeof setInterval>;
	@observable protected _isSubscribed: boolean = false;
	@observable protected _liveScoreDisposer?: ReturnType<typeof reaction>;
	@observable protected _changesDisposer?: ReturnType<typeof reaction>;

	constructor(
		@inject(Bindings.ChecksumStore) private _checksumStore: IChecksumStore,
		@inject(Bindings.RoundsStore) private _roundsStore: IRoundsStore
	) {
		makeAutoObservable(this);
		this._actions = this.generateActions();
	}

	@computed
	private get changes() {
		return Object.keys(this._checksumStore.changedChecksums);
	}

	@computed
	private get selectedRound() {
		return this._roundsStore.selectedRound;
	}

	@computed
	private get isLive() {
		return isEqual(this.selectedRound?.status, RoundStatus.Playing);
	}

	@action
	public subscribeLiveScoring() {
		if (this._isSubscribed) return;

		this._isSubscribed = true;
		void this._checksumStore.fetchChecksums();

		this._interval = setInterval(() => {
			void this._checksumStore.fetchChecksums();
		}, LIVE_SCORING_FETCH_TIMING);
	}

	/**
	 * Stop checking changes
	 * called on dispose
	 * or you can call it when you want to stop listen checksums, for example on the end of the game match/round/etc.
	 */
	@action
	public unsubscribeLiveScoring() {
		this._isSubscribed = false;

		if (this._interval) {
			clearInterval(this._interval);
		}
	}

	/**
	 * Check changed checksums and call actions
	 */
	@action
	private callActions = () => {
		this.changes.forEach((key) => {
			const action = this._actions[key];

			if (action && typeof action === "function") {
				void action();
			}
		});
	};

	/**
	 * Provide object of files you want to update
	 * for example: rounds
	 */
	private generateActions(): IChecksumAction {
		return {
			rounds: () => this._roundsStore.fetchRounds(),
		};
	}

	dispose(): void {
		this.unsubscribeLiveScoring();
		this._liveScoreDisposer?.();
		this._changesDisposer?.();
		clearTimeout(this._timerId);
	}

	init(): void {
		this._liveScoreDisposer = reaction(
			() => {
				return this.isLive;
			},
			() => {
				this.unsubscribeLiveScoring();

				if (this.isLive) {
					this.subscribeLiveScoring();
				}
			},
			{fireImmediately: true}
		);

		this._changesDisposer = reaction(() => this.changes, this.callActions, {
			fireImmediately: true,
		});

		const firstMatch = first(this.selectedRound?.tournaments);

		if (firstMatch) {
			const date1 = DateTime.fromISO(firstMatch.date);
			const date2 = DateTime.now();
			const milliseconds = date1.diff(date2, "milliseconds").milliseconds + 2000; // 2 SEC;

			if (milliseconds > 0 && milliseconds < MAX_TIMER_DELAY) {
				this._timerId = setTimeout(() => {
					this.subscribeLiveScoring();
				}, MAX_TIMER_DELAY) as unknown as number;
			}
		}
	}
}
